import { IProductItem } from '@/types/product';
import { ApiService } from './api.service';
import { endpoints } from './endpoints';
import { ChoicesOrderStatus } from '@/types';
import type { ReplaceMilestonesPayload } from '@/sections/management/accounts/orders/cancellation-order-detail/dialogs/replacement-milestones/types/milestone-replacement.types';

export interface ExistingDraftResponse {
  status: 'error';
  message: string;
}

export type CreateCancellationOrderResponse = CancellationOrderResponseDto | ExistingDraftResponse;

export interface CancellationMilestoneDto {
  _id: string;
  period: number;
  date: string;
  amount: number;
  status: string;
  isNiif15: boolean;
  originalMilestone?: {
    _id: string;
    paymentDate: string;
    milestonePeriod: number;
    total: number;
    status: string;
  };
}

export enum CancellationMilestoneAction {
  Cancelled = 'cancelled',
  Reversed = 'reversed',
}

export enum CancellationMode {
  CREATE = 'create',
  EDIT = 'edit',
}

export interface CancellationMilestoneWithAction extends CancellationMilestoneDto {
  action: CancellationMilestoneAction;
  name?: string;
  originalStatus: string;
  product?: IProductItem;
  orderItemId?: string;
  milestoneIds?: string[];
}

export interface CancellationOrderResponseDto {
  requestedAt: string;
  createdAt: string;
  _id: string;
  cancellationNumber: number;
  status: string;
  order: {
    _id: string;
    number: number;
    status: string;
    orderType: string;
  };
  organization: {
    _id: string;
    name: string;
    country: { _id: string; name: string; flag_image: string };
    segment: { _id: string; name: string };
    owner: {
      _id: string;
      firstName: string;
      lastName: string;
      email: string;
      avatar?: { url: string };
    };
  };
  contacts: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  }[];
  requestedBy: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    avatar?: { url: string };
  };
  items: {
    _id: string;
    orderItem: {
      product: any;
      _id: string;
      name?: string;
      quantity: number;
      sku: { _id: string; name: string; code: string };
      plan: { _id: string; name: string };
    };
    milestones: (CancellationMilestoneDto & {
      action?: CancellationMilestoneAction.Cancelled | CancellationMilestoneAction.Reversed;
      originalStatus?: string;
      name?: string;
      orderItemId?: string;
    })[];
  }[];
}

export interface CancellableItemDto {
  _id: string;
  sku: string;
  plan: string;
  quantity: number;
  productName: string;
  customerBillingFrequency: string;
  billedMilestones: CancellationMilestoneDto[];
  milestones: (CancellationMilestoneDto & {
    period?: number;
    milestonePeriod?: number;
  })[];
}

export interface CancellableItemsResponseDto {
  orderNumber: number;
  items: CancellableItemDto[];
}

export interface CreateCancellationOrderPayload {
  orderId: string;
  items: CancellationOrderItem[];
}

export interface UpdateCancellationOrderPayload {
  orderId: string;
  items: CancellationOrderItem[];
}

export interface CancellationOrderItem {
  orderItemId: string;
  milestoneIds: string[];
}

export interface ReplaceMilestonesResponse {
  success: boolean;
  message?: string;
  cancellationOrder?: CancellationOrderResponseDto;
}

export class CancellationApiService extends ApiService {
  async createCancellationOrder(
    payload: CreateCancellationOrderPayload
  ): Promise<CreateCancellationOrderResponse> {
    const response = await this.post(endpoints.cancellation.createOrder(), payload);
    return response.data || response;
  }

  async updateCancellationOrder(
    id: string,
    payload: UpdateCancellationOrderPayload
  ): Promise<CancellationOrderResponseDto> {
    const response = await this.patch(endpoints.cancellation.updateOrder(id), payload);
    return response.data || response;
  }

  async getCancellationOrderById(id: string): Promise<CancellationOrderResponseDto> {
    const response = await this.get(endpoints.cancellation.getOrderById(id));
    return response.data || response;
  }

  async getItemsForCancellation(orderId: string): Promise<CancellableItemsResponseDto> {
    return this.get(endpoints.cancellation.cancellableItems(orderId));
  }

  async getCancellationOrders(organizationId: string): Promise<CancellationOrderResponseDto[]> {
    const response = await this.get(endpoints.cancellation.getOrders(organizationId));
    return response.data || response;
  }

  async getCancellationOrdersByPurchaseOrder(
    orderId: string
  ): Promise<CancellationOrderResponseDto[]> {
    return this.get(endpoints.cancellation.getCancellationOrdersByPurchaseOrder(orderId));
  }

  async deleteCancellationOrder(id: string): Promise<void> {
    await this.delete(endpoints.cancellation.deleteOrder(id));
  }

  async confirmCancellationOrder(id: string): Promise<CancellationOrderResponseDto> {
    return this.post(endpoints.cancellation.confirmOrder(id), {});
  }

  async replaceMilestones(payload: ReplaceMilestonesPayload): Promise<ReplaceMilestonesResponse> {
    const response = await this.post(endpoints.cancellation.replaceMilestones(), payload);
    return response.data || response;
  }
}

export const isCancellationOrderDraft = (order: CancellationOrderResponseDto): boolean => {
  return order.status === ChoicesOrderStatus.created;
};

export const filterMilestonesByAction = (
  milestones: CancellationMilestoneWithAction[],
  action: CancellationMilestoneAction
): CancellationMilestoneWithAction[] => {
  return milestones.filter((milestone) => milestone.action === action);
};

export const calculateCancelledTotal = (milestones: CancellationMilestoneWithAction[]): number => {
  return filterMilestonesByAction(milestones, CancellationMilestoneAction.Cancelled).reduce(
    (total, milestone) => total + milestone.amount,
    0
  );
};

export const hasNiif15Milestones = (milestones: CancellationMilestoneWithAction[]): boolean => {
  return milestones.some((milestone) => milestone.isNiif15);
};

export const findDraftCancellationOrder = (
  orders: CancellationOrderResponseDto[],
  orderId: string
): CancellationOrderResponseDto | undefined => {
  return orders.find((order) => order.order._id === orderId && isCancellationOrderDraft(order));
};

export const cancellationApiService = new CancellationApiService();
export const {
  getItemsForCancellation,
  createCancellationOrder,
  updateCancellationOrder,
  getCancellationOrderById,
  getCancellationOrders,
  deleteCancellationOrder,
  confirmCancellationOrder,
  replaceMilestones,
} = cancellationApiService;
